'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { 
  Package, TrendingUp, TrendingDown, DollarSign, Calendar,
  Loader2, AlertTriangle, ShoppingCart, Activity
} from 'lucide-react';

import { DataTable } from '@/components/dashboard/DataTable';
import { 
  fetchDailyOrders,
  fetchMonthlyOrders,
  fetchOperationsMetrics,
  formatCurrency, 
  formatPercent
} from '@/lib/api-client';
import type { TableColumn } from '@/types/dashboard';

const COLORS = ['#3B82F6', '#22C55E', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#14B8A6', '#F97316'];

// Card Component
const Card = ({ children, className = "", isDark = true }: { children: React.ReactNode; className?: string; isDark?: boolean }) => (
  <div className={`backdrop-blur-md border rounded-xl p-5 transition-colors duration-300 ${
    isDark 
      ? 'bg-neutral-900/50 border-neutral-800' 
      : 'bg-white border-gray-200 shadow-sm'
  } ${className}`}>
    {children}
  </div>
);

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, isDark = true }: any) => (
  <Card isDark={isDark}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className={`text-sm font-medium ${isDark ? 'text-neutral-400' : 'text-gray-600'}`}>
          {title}
        </p>
        <h3 className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {value}
        </h3>
        {subtitle && (
          <p className={`text-xs mt-1 ${isDark ? 'text-neutral-500' : 'text-gray-500'}`}>
            {subtitle}
          </p>
        )}
      </div>
      {Icon && (
        <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
          <Icon size={20} className="text-blue-500" />
        </div>
      )}
    </div>
    {trend !== undefined && (
      <div className={`mt-3 flex items-center gap-1 text-sm ${
        trend >= 0 ? 'text-emerald-400' : 'text-rose-400'
      }`}>
        {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
        <span>{Math.abs(trend)}% vs last period</span>
      </div>
    )}
  </Card>
);

export default function OrdersPage() {
  const [dailyOrders, setDailyOrders] = useState<any[]>([]);
  const [monthlyOrders, setMonthlyOrders] = useState<any[]>([]);
  const [operations, setOperations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode] = useState(true);
  const [timeRange, setTimeRange] = useState<'30' | '60' | '90'>('30');

  // Load order data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        
        const days = parseInt(timeRange);
        const [daily, monthly, ops] = await Promise.all([
          fetchDailyOrders(days),
          fetchMonthlyOrders(12),
          fetchOperationsMetrics(days)
        ]);
        
        setDailyOrders(daily);
        setMonthlyOrders(monthly);
        setOperations(ops);
        console.log('✅ Loaded order data:', { daily: daily.length, monthly: monthly.length });
      } catch (err) {
        console.error('❌ Failed to load order data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [timeRange]);

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!dailyOrders.length) return null;

    const totalOrders = dailyOrders.reduce((sum, day) => sum + (day.order_count || 0), 0);
    const totalRevenue = dailyOrders.reduce((sum, day) => sum + (day.total_revenue || 0), 0);
    const avgOrderValue = totalRevenue / totalOrders;
    const avgDailyOrders = totalOrders / dailyOrders.length;

    // Calculate trend (compare first half vs second half)
    const mid = Math.floor(dailyOrders.length / 2);
    const firstHalf = dailyOrders.slice(0, mid);
    const secondHalf = dailyOrders.slice(mid);
    
    const firstHalfAvg = firstHalf.reduce((sum, d) => sum + (d.order_count || 0), 0) / firstHalf.length;
    const secondHalfAvg = secondHalf.reduce((sum, d) => sum + (d.order_count || 0), 0) / secondHalf.length;
    const trend = ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100;

    return {
      totalOrders,
      totalRevenue,
      avgOrderValue,
      avgDailyOrders: Math.round(avgDailyOrders),
      trend: Math.round(trend * 10) / 10
    };
  }, [dailyOrders]);

  // Prepare chart data for daily volume
  const dailyChartData = useMemo(() => {
    return dailyOrders.map(day => ({
      date: new Date(day.order_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      orders: day.order_count || 0,
      revenue: day.total_revenue || 0,
      aov: (day.total_revenue || 0) / (day.order_count || 1)
    }));
  }, [dailyOrders]);

  // Prepare monthly trend data
  const monthlyChartData = useMemo(() => {
    return monthlyOrders.map(month => ({
      month: new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      orders: month.order_count || 0,
      revenue: month.total_revenue || 0,
      freight: month.freight_revenue || 0,
      pickpack: month.pickpack_revenue || 0,
      storage: month.storage_revenue || 0
    }));
  }, [monthlyOrders]);

  // Business unit distribution
  const businessUnitData = useMemo(() => {
    if (!monthlyOrders.length) return [];
    
    const totals = monthlyOrders.reduce((acc, month) => ({
      freight: acc.freight + (month.freight_revenue || 0),
      pickpack: acc.pickpack + (month.pickpack_revenue || 0),
      storage: acc.storage + (month.storage_revenue || 0)
    }), { freight: 0, pickpack: 0, storage: 0 });

    return [
      { name: 'Freight', value: totals.freight, orders: Math.round(totals.freight / 10) },
      { name: 'Pick & Pack', value: totals.pickpack, orders: Math.round(totals.pickpack / 50) },
      { name: 'Storage', value: totals.storage, orders: Math.round(totals.storage / 100) }
    ].filter(item => item.value > 0);
  }, [monthlyOrders]);

  // Table columns for monthly breakdown
  const monthlyColumns: TableColumn[] = [
    {
      key: 'month',
      label: 'Month',
      sortable: true
    },
    {
      key: 'order_count',
      label: 'Orders',
      sortable: true,
      align: 'right',
      formatter: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'total_revenue',
      label: 'Revenue',
      sortable: true,
      align: 'right',
      formatter: formatCurrency
    },
    {
      key: 'freight_revenue',
      label: 'Freight',
      sortable: true,
      align: 'right',
      formatter: formatCurrency
    },
    {
      key: 'pickpack_revenue',
      label: 'Pick & Pack',
      sortable: true,
      align: 'right',
      formatter: formatCurrency
    },
    {
      key: 'storage_revenue',
      label: 'Storage',
      sortable: true,
      align: 'right',
      formatter: formatCurrency
    },
    {
      key: 'avg_order_value',
      label: 'AOV',
      sortable: true,
      align: 'right',
      formatter: formatCurrency
    }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className={isDarkMode ? 'text-neutral-400' : 'text-gray-600'}>
            Loading order analytics...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Failed to load data
          </p>
          <p className={isDarkMode ? 'text-neutral-400' : 'text-gray-600'}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Order Analytics
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
            Order volume, trends, and performance metrics
          </p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex gap-2">
          {(['30', '60', '90'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-blue-500 text-white'
                  : isDarkMode
                    ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range} Days
            </button>
          ))}
        </div>
      </div>

      {/* Summary Metrics */}
      {summaryMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Orders"
            value={summaryMetrics.totalOrders.toLocaleString()}
            subtitle={`${timeRange} days`}
            icon={Package}
            trend={summaryMetrics.trend}
            isDark={isDarkMode}
          />
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(summaryMetrics.totalRevenue)}
            subtitle={`${timeRange} days`}
            icon={DollarSign}
            isDark={isDarkMode}
          />
          <MetricCard
            title="Avg Order Value"
            value={formatCurrency(summaryMetrics.avgOrderValue)}
            subtitle="Per order"
            icon={ShoppingCart}
            isDark={isDarkMode}
          />
          <MetricCard
            title="Avg Daily Orders"
            value={summaryMetrics.avgDailyOrders.toLocaleString()}
            subtitle="Per day"
            icon={Activity}
            isDark={isDarkMode}
          />
        </div>
      )}

      {/* Charts Row 1: Daily Volume & AOV Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Order Volume */}
        <Card isDark={isDarkMode}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Daily Order Volume
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
              <XAxis 
                dataKey="date" 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280', fontSize: 11 }}
              />
              <YAxis 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
                formatter={(value: any) => value.toLocaleString()}
              />
              <Line 
                type="monotone" 
                dataKey="orders" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', r: 3 }}
                name="Orders"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Average Order Value Trend */}
        <Card isDark={isDarkMode}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Average Order Value Trend
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
              <XAxis 
                dataKey="date" 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280', fontSize: 11 }}
              />
              <YAxis 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280' }}
                tickFormatter={(value) => `$${value.toFixed(0)}`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
                formatter={(value: any) => formatCurrency(value)}
              />
              <Line 
                type="monotone" 
                dataKey="aov" 
                stroke="#22C55E" 
                strokeWidth={2}
                dot={{ fill: '#22C55E', r: 3 }}
                name="AOV"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts Row 2: Monthly Trends & Business Unit Mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Revenue by Business Unit */}
        <Card isDark={isDarkMode} className="lg:col-span-2">
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Monthly Revenue by Business Unit
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#e5e7eb'} />
              <XAxis 
                dataKey="month" 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280', fontSize: 11 }}
              />
              <YAxis 
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                tick={{ fill: isDarkMode ? '#999' : '#6b7280' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
                formatter={(value: any) => formatCurrency(value)}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="freight" 
                stackId="1"
                stroke="#3B82F6" 
                fill="#3B82F6"
                fillOpacity={0.6}
                name="Freight"
              />
              <Area 
                type="monotone" 
                dataKey="pickpack" 
                stackId="1"
                stroke="#22C55E" 
                fill="#22C55E"
                fillOpacity={0.6}
                name="Pick & Pack"
              />
              <Area 
                type="monotone" 
                dataKey="storage" 
                stackId="1"
                stroke="#8B5CF6" 
                fill="#8B5CF6"
                fillOpacity={0.6}
                name="Storage"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Business Unit Distribution */}
        <Card isDark={isDarkMode}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Revenue by Business Unit
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={businessUnitData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${((entry.value / businessUnitData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {businessUnitData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
                formatter={(value: any) => formatCurrency(value)}
              />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Legend */}
          <div className="mt-4 space-y-2">
            {businessUnitData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className={isDarkMode ? 'text-neutral-300' : 'text-gray-700'}>
                    {item.name}
                  </span>
                </div>
                <span className={isDarkMode ? 'text-neutral-400' : 'text-gray-600'}>
                  {formatCurrency(item.value)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Monthly Detail Table */}
      <Card isDark={isDarkMode}>
        <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Monthly Order Breakdown
        </h2>
        <DataTable
          columns={monthlyColumns}
          data={monthlyOrders.map(month => ({
            ...month,
            month: new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            avg_order_value: (month.total_revenue || 0) / (month.order_count || 1)
          }))}
          pageSize={12}
          searchable={false}
          isDark={isDarkMode}
        />
      </Card>
    </div>
  );
}
